import React, { useState, useEffect } from 'react';
import './setupcss/MetaSetup.css';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { META_APP_ID, CTW_API_ENDPOINT } from '../../utils';
import coreAPIService from '../../apiServices/coreAPIService';
import { AdAccount, ClientDetails, Connectors, FbPage } from "../../interfaces/setup";
import { Modal, Button, Card, Avatar, Typography, Alert, Pagination, Input } from 'antd';
import { SyncOutlined, FacebookOutlined, SearchOutlined } from '@ant-design/icons';
import { toast } from '../../common/Popover';
import Loader from '../../components/Loader';
import CommonButton from '../../common/CommonButton';
import { Phone } from 'lucide-react';
import confirm from 'antd/es/modal/confirm';
import { useMetaSetup } from '../../context/MetaSetupContext';
import { useAuthOrg } from '../../context/AuthOrgContext';

const { Title, Text } = Typography;

interface MetaSetupProps {
  metaConnectStatus: boolean;
  setMetaConnectStatus: (status: boolean) => void;
  connectionData: Connectors | null;
  setConnectionData: React.Dispatch<React.SetStateAction<Connectors | null>>;
  metaData: {
    fbPageList: FbPage[]
    adAccountList: AdAccount[]
    wabaNumberList: ClientDetails[]
  }
  setupStatusCheck: () => void;
}



const PAGE_SIZE = 5;
const MetaSetup: React.FC<MetaSetupProps> = ({
  metaConnectStatus,
  setMetaConnectStatus,
  connectionData,
  setConnectionData,
  metaData,
  setupStatusCheck
}) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [fbPages, setFbPages] = useState<FbPage[]>([]);

  // Pagination & Search states
  const [adPage, setAdPage] = useState(1);
  const [fbPage, setFbPage] = useState(1);
  const [wabaPage, setWabaPage] = useState(1);
  const [adSearch, setAdSearch] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [wabaSearch, setWabaSearch] = useState('');
  const { selectedAdAccount, setSelectedAdAccount, orgWabaData } = useMetaSetup();
  const [modal, contextHolder] = Modal.useModal();
  const { refreshSession } = useAuthOrg();

  // --- HANDLE CLICK (SET + SAVE TO CONTEXT + API CALL) ---
  const handleSelect = async (id: string, name: string) => {
    try {
      // Call API to set default ad account
      const response = await coreAPIService.post<any>(
        CTW_API_ENDPOINT,
        `v2/util/ad-account/default?ad_account_id=${id}`,
        {}
      );

      if (response.meta.code === 200) {
        // Update context
        setSelectedAdAccount({ id, name });

        // Update local state to reflect is_default
        setAdAccounts(prevAccounts => {
          const selected = prevAccounts.find(acc => acc.id === id);
          const others = prevAccounts.filter(acc => acc.id !== id);

          if (!selected) return prevAccounts;

          return [
            { ...selected, is_default: true },
            ...others.map(acc => ({ ...acc, is_default: false }))
          ];
        });


        toast('success', 'Default ad account set successfully');
      } else {
        toast('error', response.meta.message || 'Failed to set default ad account');
      }
    } catch (error: any) {
      toast('error', error.message || 'Failed to set default ad account');
    }
  };



  // Load from metaData on mount
  useEffect(() => {
    if (connectionData) {
      setAdAccounts((metaData.adAccountList || []).filter(acc => acc.accountStatus === 1));
      setFbPages(metaData.fbPageList || []);
      setAdPage(1);
      setFbPage(1);
      setWabaPage(1);
      setAdSearch(''); setPageSearch(''); setWabaSearch('');

      // Find default ad account from API response
      const defaultAccount = metaData.adAccountList?.find(acc => acc.isDefault);
      if (defaultAccount) {
        setSelectedAdAccount({ id: defaultAccount.id, name: defaultAccount.name });
      } else if (metaData.adAccountList?.[0]) {
        // Fallback to first account if no default is set
      }
    }
  }, [connectionData, metaData.adAccountList, metaData.fbPageList, metaConnectStatus]);

  // Facebook Signed Login
  const fbSignedLogin = async (response: any) => {
    setLoading(true);
    try {
      const res = await coreAPIService.post<any>(CTW_API_ENDPOINT, 'v2/meta-integration/app-connect', response);

      if (res.meta.code === 200) {
        await setupStatusCheck();
        setMetaConnectStatus(true);
        refreshSession();

        toast("success", "Facebook account connected successfully!");
      } else {
        toast("error", res.message || "Login failed");
      }
    } catch (error: any) {
      toast("error", error.message || "Failed to connect Facebook");
    } finally {
      setLoading(false);
    }
  };

  // Disconnect
  const fbSignedLogout = async () => {
    const confirmed = await modal.confirm({
      title: 'Disconnect Facebook?',
      content: 'This will remove all connected data. You can reconnect anytime.',
    });

    if (!confirmed) return;

    try {
      const res = await coreAPIService.post<any>(CTW_API_ENDPOINT, 'v2/meta-integration/app-disconnect');
      if (res.meta.code === 200) {
        setConnectionData(null);
        setMetaConnectStatus(false);
        setAdAccounts([]);
        setFbPages([]);
        toast("success", "Disconnected successfully");
      }
    } catch (error) {
      toast("error", "Failed to disconnect");
    }
  };

  // Sync Campaigns
  const syncCampaigns = async () => {
    setSyncing(true);
    try {
      if (selectedAdAccount == null) {
        toast("error", "Please select an ad account");
        return;
      }
      const res = await coreAPIService.post<{ meta: { code: number, message: string } }>(CTW_API_ENDPOINT, `v2/meta-integration/adaccounts/${selectedAdAccount.id}/ctwa-campaigns/sync`);
      if (res.meta.code === 200) {
        toast("success", res.meta.message);
      } else {
        toast("warning", res.meta.message);
      }
    } catch (error) {
      toast("error", "Sync error");
    } finally {
      setSyncing(false);
    }
  };
  // Filter data 
  // Filtered Data States
  const [filteredAdAccounts, setFilteredAdAccounts] = useState<AdAccount[]>([]);
  const [filteredFbPages, setFilteredFbPages] = useState<FbPage[]>([]);
  const [filteredWaba, setFilteredWaba] = useState<ClientDetails[]>([]);

  // Paginated Data States
  const [paginatedAds, setPaginatedAds] = useState<AdAccount[]>([]);
  const [paginatedPages, setPaginatedPages] = useState<FbPage[]>([]);
  const [paginatedWaba, setPaginatedWaba] = useState<ClientDetails[]>([]);

  // Effect to filter Ad Accounts
  useEffect(() => {
    const filtered = adAccounts.filter(item =>
      (item.name + item.id)
        .toLowerCase()
        .includes(adSearch.toLowerCase())
    );

    const selected = filtered.find(acc => acc.isDefault);
    const others = filtered.filter(acc => !acc.isDefault);

    setFilteredAdAccounts(
      selected ? [selected, ...others] : filtered
    );

    // Reset to page 1 if search changes is handled in the input onChange, 
    // but we should ensure pagination updates when filtered data changes
  }, [adAccounts, adSearch, metaConnectStatus]);

  // Effect to filter FB Pages
  useEffect(() => {
    const filtered = fbPages.filter(item =>
      (item.name + item.id).toLowerCase().includes(pageSearch.toLowerCase())
    );
    setFilteredFbPages(filtered);
  }, [fbPages, pageSearch, metaConnectStatus]);

  // Effect to filter WhatsApp Numbers - now filters org WABA data
  useEffect(() => {
    const flattenedWaba: Array<{ orgId: string; orgName: string; waba: ClientDetails }> = [];
    orgWabaData.forEach(org => {
      org.wabaDetails.forEach(waba => {
        const searchText = `${waba.subdomain}${waba.wabaNumber}${waba.clientId}${waba.wabaId}${org.orgId}`.toLowerCase();
        if (searchText.includes(wabaSearch.toLowerCase())) {
          flattenedWaba.push({ orgId: org.orgId, orgName: org.orgName, waba: waba as any });
        }
      });
    });
    setFilteredWaba(flattenedWaba as any);
  }, [orgWabaData, wabaSearch, metaConnectStatus]);

  // Effect to paginate Ad Accounts
  useEffect(() => {
    const sliced = filteredAdAccounts.slice(
      (adPage - 1) * PAGE_SIZE,
      adPage * PAGE_SIZE
    );
    setPaginatedAds(sliced);
  }, [filteredAdAccounts, adPage]);

  // Effect to paginate FB Pages
  useEffect(() => {
    const sliced = filteredFbPages.slice(
      (fbPage - 1) * PAGE_SIZE,
      fbPage * PAGE_SIZE
    );
    setPaginatedPages(sliced);
  }, [filteredFbPages, fbPage]);

  // Effect to paginate WhatsApp Numbers
  useEffect(() => {
    const sliced = filteredWaba.slice(
      (wabaPage - 1) * PAGE_SIZE,
      wabaPage * PAGE_SIZE
    );
    setPaginatedWaba(sliced);
  }, [filteredWaba, wabaPage]);

  const showConfirm = (id: string, name: string) => {
    confirm({
      title: "Set Default Meta Ad Account",
      content: `Are you sure you want to set "${name}" as your default Meta Ad Account ? `,
      okText: "Yes, Set Default",
      cancelText: "Cancel",
      centered: true,
      onOk: () => handleSelect(id, name),
    });
  };

  return (
    <>
      {loading && <Loader closable={false} />}
      {contextHolder}

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {/* Header: Connect Facebook */}
        <Card className="shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <Title level={4} className="flex items-center gap-2">
                <FacebookOutlined className="text-blue-600" />
                Connect Facebook Account
              </Title>
              <Text type="secondary">
                Connect to fetch ad accounts, pages, and WhatsApp details automatically.
              </Text>
            </div>

            {!metaConnectStatus ? (
              <FacebookLogin
                appId={META_APP_ID}
                scope="pages_read_engagement, business_management, ads_read, ads_management, pages_show_list"
                onSuccess={fbSignedLogin}
                onFail={(err) => toast("error", "Facebook login failed")}
                className="ant-btn ant-btn-primary"
                children={<CommonButton label="Connect with Facebook" />}
              />
            ) : (
              <CommonButton type={"danger"} onClick={fbSignedLogout} label='Disconnect' />
            )}
          </div>
        </Card>

        {/* Only show data if connected */}
        <>
          {/* Search Filters */}
          <Card className="shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Search ad accounts..."
                prefix={<SearchOutlined />}
                value={adSearch}
                onChange={(e) => {
                  setAdSearch(e.target.value);
                  setAdPage(1);
                }}
                allowClear
                disabled={!metaConnectStatus}
              />
              <Input
                placeholder="Search FB pages..."
                prefix={<SearchOutlined />}
                value={pageSearch}
                onChange={(e) => {
                  setPageSearch(e.target.value);
                  setFbPage(1);
                }}
                allowClear
                disabled={!metaConnectStatus}
              />
              <Input
                placeholder="Search WABAs..."
                prefix={<SearchOutlined />}
                value={wabaSearch}
                onChange={(e) => {
                  setWabaSearch(e.target.value);
                  setWabaPage(1);
                }}
                allowClear
              />
            </div>
          </Card>

          {/* Table-like View */}

          <Card className="shadow-md p-4 rounded-2xl">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Ad Accounts */}
              {metaConnectStatus ? <div className="flex flex-col" style={{ minHeight: '400px' }}>
                <div className="font-semibold text-base mb-4 flex items-center justify-between">
                  <div className="truncate">
                    <FacebookOutlined className="mr-2 text-blue-600" />
                    Meta Ad Accounts ({filteredAdAccounts.length})
                  </div>
                  {!selectedAdAccount && (
                    <span className="text-sm text-red-400 whitespace-nowrap ml-2">Select default</span>
                  )}
                </div>

                <div className="flex-grow">
                  {paginatedAds.length > 0 ? (
                    <div className="space-y-2">
                      {paginatedAds.map((item) => {
                        const isSelected = selectedAdAccount?.id === item.id;
                        return (
                          <div
                            key={item.id}
                            onClick={() => showConfirm(item.id, item.name)}
                            className={`flex items-center gap-2 p-2.5 cursor-pointer border rounded-lg transition-all 
                      ${isSelected ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:border-blue-200 hover:bg-gray-50"} `}
                          >
                            <Avatar size="small" style={{ backgroundColor: "#1890ff" }}>
                              {item.name[0]}
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">
                                {item.name}
                                {isSelected && (
                                  <span className="text-blue-400 ml-2">Default</span>
                                )}
                              </div>
                              <Text type="secondary" className="text-xs">
                                ID: {item.id}
                              </Text>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Alert message="No ad accounts found" type="info" showIcon />
                  )}
                </div>

                <div className="mt-4">
                  {filteredAdAccounts.length > PAGE_SIZE && (
                    <div className="flex justify-center">
                      <Pagination
                        current={adPage}
                        pageSize={PAGE_SIZE}
                        total={filteredAdAccounts.length}
                        onChange={setAdPage}
                        simple
                        showSizeChanger={false}
                        showTotal={(total, range) =>
                          `${range[0]}-${range[1]} of ${total}`
                        }
                      />
                    </div>
                  )}
                </div>
              </div> : (
                <Card className="text-center py-12 bg-gray-50">
                  <Text type="secondary" className="text-lg">
                    Connect your Facebook account to view ad accounts.
                  </Text>
                </Card>
              )}

              {/* Facebook Pages */}
              {metaConnectStatus ? (<div className="flex flex-col" style={{ minHeight: '400px' }}>
                <div className="font-semibold text-base mb-4 truncate">
                  <FacebookOutlined className="mr-2 text-blue-600" />
                  Facebook Pages ({filteredFbPages.length})
                </div>

                <div className="flex-grow">
                  {paginatedPages.length > 0 ? (
                    <div className="space-y-2">
                      {paginatedPages.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-2 p-2.5 border rounded-lg transition-all hover:bg-gray-50"
                        >
                          <Avatar size="small" style={{ backgroundColor: "#1890ff" }}>
                            {item.name[0]}
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{item.name}</div>
                            <Text type="secondary" className="text-xs">
                              Business: {item.business}
                            </Text>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert message="No Facebook pages found" type="info" showIcon />
                  )}
                </div>

                <div className="mt-4">
                  {filteredFbPages.length > PAGE_SIZE && (
                    <div className="flex justify-center">
                      <Pagination
                        current={fbPage}
                        pageSize={PAGE_SIZE}
                        total={filteredFbPages.length}
                        onChange={setFbPage}
                        simple
                        showSizeChanger={false}
                        showTotal={(total, range) =>
                          `${range[0]}-${range[1]} of ${total}`
                        }
                      />
                    </div>
                  )}
                </div>
              </div>) : (
                <Card className="text-center py-12 bg-gray-50">
                  <Text type="secondary" className="text-lg">
                    Connect your Facebook account to view meta pages.
                  </Text>
                </Card>
              )}

              <div className="flex flex-col" style={{ minHeight: '400px' }}>
                <div className="flex justify-between items-center mb-4">
                  <div className="font-semibold text-base truncate">
                    <Phone className="inline mr-2 h-5 w-5 text-green-600" />
                    WhatsApp Business Numbers ({filteredWaba.length})
                  </div>
                </div>

                <div className="flex-grow">
                  {paginatedWaba.length > 0 ? (
                    <div className="space-y-2">
                      {paginatedWaba.map((item: any, index: number) => (
                        <div
                          key={`${item.orgId}-${item.waba?.wabaId || item.clientWaRegisteredNumber}-${index}`}
                          className="flex items-center gap-2 p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                        >
                          {item.waba ? (
                            <>
                              <Avatar style={{ backgroundColor: '#25D366' }} size="small">
                                <Phone className="h-3 w-3" />
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{item.waba.wabaNumber} | <Text type="secondary" className="text-xs">
                                  {item.waba.subdomain}
                                </Text></div>
                                <span className="text-xs text-gray-500 truncate" title={item.orgName}>Account: {item.orgName}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <Avatar size="small" style={{ backgroundColor: "#25D366" }}>
                                <Phone className="h-3 w-3" />
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{item.clientSubdomain}</div>
                                <Text type="secondary" className="text-xs">
                                  Number: {item.clientWaRegisteredNumber}
                                </Text>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert message="No WhatsApp numbers found" type="info" showIcon />
                  )}
                </div>

                <div className="mt-4">
                  {filteredWaba.length > PAGE_SIZE && (
                    <div className="flex justify-center">
                      <Pagination
                        current={wabaPage}
                        pageSize={PAGE_SIZE}
                        total={filteredWaba.length}
                        onChange={setWabaPage}
                        simple
                        showSizeChanger={false}
                        showTotal={(total, range) =>
                          `${range[0]}-${range[1]} of ${total}`
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Sync Button */}
          <div className="text-center">
            <Button
              size="large"
              type="primary"
              icon={<SyncOutlined spin={syncing} />}
              onClick={syncCampaigns}
              disabled={syncing || selectedAdAccount == null}
              className="bg-[#1F344C] text-white"
            >
              {syncing ? 'Syncing...' : 'Sync Ads & Insights'}
            </Button>
          </div>
        </>
      </div >
    </>
  );
};

export default MetaSetup;